import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input, Popover, PopoverTrigger, PopoverContent } from '@vnetwork/ui/primitives';
import styles from './DateTimeRangePicker.module.css';

export interface DateTimeRange {
  /** ISO datetime string (or '' when unset). */
  from: string;
  to: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function toDate(iso: string): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** HH:mm of an ISO datetime, or the fallback when unset. */
function timeOf(iso: string, fallback: string): string {
  const d = toDate(iso);
  return d ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : fallback;
}

/** Combine a picked calendar day with an HH:mm time into a local ISO datetime. */
function combine(day: Date, time: string): string {
  const [h, m] = time.split(':').map(Number);
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), h || 0, m || 0, 0, 0).toISOString();
}

function display(iso: string): string {
  const d = toDate(iso);
  if (!d) return '';
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 6×7 day grid (Sunday-first) covering the month containing `view`. */
function monthGrid(view: Date): Date[] {
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  const offset = first.getDay(); // Su=0 … Sa=6
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - offset);
  return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
}

/**
 * DS-native date-AND-time range picker: a Select-style trigger opening a floating
 * calendar (with the selected range highlighted edge-to-edge + hover preview) plus
 * a start/end time row. First calendar click stages the start day, the second commits
 * the range (start day+time → end day+time); clicking before the start restarts. Built
 * on the DS `Popover` (portals out of the flow), so it floats over the layout and is
 * never clipped by an ancestor's overflow — reusable anywhere a datetime window is edited.
 */
export function DateTimeRangePicker({
  value,
  onChange,
  disabled,
  placeholder = 'Select date & time range',
}: {
  value: DateTimeRange;
  onChange: (next: DateTimeRange) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  // Draft range: calendar endpoints are date-only, times live in the inputs below.
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const [hover, setHover] = useState<Date | null>(null);
  const [fromTime, setFromTime] = useState('00:00');
  const [toTime, setToTime] = useState('23:59');
  const [view, setView] = useState(() => new Date());

  // Seed the draft from the committed value each time the panel opens.
  useEffect(() => {
    if (!open) return;
    const from = toDate(value.from);
    const to = toDate(value.to);
    setRangeStart(from ? startOfDay(from) : null);
    setRangeEnd(to ? startOfDay(to) : null);
    setFromTime(timeOf(value.from, '00:00'));
    setToTime(timeOf(value.to, '23:59'));
    setView(to ?? from ?? new Date());
    setHover(null);
  }, [open, value.from, value.to]);

  const today = new Date();

  // Calendar click: first sets the start, second commits the range (restart if before).
  const pickDay = (d: Date) => {
    if (!rangeStart || rangeEnd) {
      setRangeStart(d);
      setRangeEnd(null);
      return;
    }
    if (d < rangeStart) {
      setRangeStart(d);
      return;
    }
    onChange({ from: combine(rangeStart, fromTime), to: combine(d, toTime) });
    setRangeEnd(d);
    setOpen(false);
  };

  // Tentative end while hovering (only when a start is staged and no end yet).
  const previewEnd = rangeStart && !rangeEnd && hover && hover >= rangeStart ? hover : null;

  const cellState = (d: Date) => {
    const end = rangeEnd ?? previewEnd;
    const isStart = rangeStart && sameDay(d, rangeStart);
    const isEnd = end && sameDay(d, end);
    const inside = rangeStart && end && d > rangeStart && d < end;
    return {
      rangeStart: !!isStart,
      rangeEnd: !!isEnd,
      endpoint: !!isStart || !!isEnd,
      inRange: !!inside && !!rangeEnd,
      preview: !!inside && !rangeEnd,
    };
  };

  const label = value.from && value.to ? `${display(value.from)} – ${display(value.to)}` : placeholder;
  const grid = useMemo(() => monthGrid(view), [view]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={styles.trigger}
          data-placeholder={value.from && value.to ? undefined : ''}
        >
          <span className={styles.triggerLabel}>
            <CalendarDays size={15} className={styles.calendarIcon} />
            {label}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" collisionPadding={12} avoidCollisions style={{ padding: 0 }}>
        <div className={styles.panel}>
          <div className={styles.hint} aria-live="polite">
            {rangeStart && !rangeEnd ? 'Select end date' : 'Select start date'}
          </div>

          <div className={styles.calHeader}>
            <button
              type="button"
              className={styles.navBtn}
              aria-label="Previous month"
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
            >
              <ChevronLeft size={16} />
            </button>
            <span className={styles.monthLabel}>
              {view.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button
              type="button"
              className={styles.navBtn}
              aria-label="Next month"
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className={styles.weekHeader}>
            {WEEKDAYS.map((w) => (
              <span key={w} className={styles.weekDay}>
                {w}
              </span>
            ))}
          </div>

          <div className={styles.grid} onMouseLeave={() => setHover(null)}>
            {grid.map((d) => {
              const st = cellState(d);
              const otherMonth = d.getMonth() !== view.getMonth();
              const cellCls = [
                styles.cell,
                st.inRange ? styles.inRange : '',
                st.preview ? styles.preview : '',
                st.rangeStart ? styles.rangeStart : '',
                st.rangeEnd ? styles.rangeEnd : '',
              ]
                .filter(Boolean)
                .join(' ');
              const dayCls = [
                styles.day,
                otherMonth ? styles.other : '',
                sameDay(d, today) ? styles.today : '',
                st.endpoint ? styles.endpoint : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <div key={d.getTime()} className={cellCls}>
                  <button
                    type="button"
                    className={dayCls}
                    onClick={() => pickDay(d)}
                    onMouseEnter={() => setHover(d)}
                  >
                    {d.getDate()}
                  </button>
                </div>
              );
            })}
          </div>

          <div className={styles.timeRow}>
            <label className={styles.timeField}>
              <span className={styles.timeLabel}>Start time</span>
              <Input type="time" value={fromTime} onChange={(e) => setFromTime(e.target.value)} />
            </label>
            <label className={styles.timeField}>
              <span className={styles.timeLabel}>End time</span>
              <Input type="time" value={toTime} onChange={(e) => setToTime(e.target.value)} />
            </label>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
